import React from 'react';
import { DollarSign, Activity, PieChart, Thermometer } from 'lucide-react';
import { formatCurrency } from '../utils/currencyUtils';
import type { ExchangeRates } from '../utils/currencyUtils';
import { GlassCard } from './common/GlassCard';

interface MarketOverviewProps {
  currency: string;
  exchangeRates: ExchangeRates;
}

export function MarketOverview({ currency, exchangeRates }: MarketOverviewProps) {
  // Format large numbers with suffixes
  const formatLargeNumber = (value: number) => {
    if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
    return `$${value.toFixed(2)}`;
  };

  const fearGreedScore = 83;

  const getFearGreedColor = (score: number) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    if (score >= 25) return 'text-orange-600';
    return 'text-red-600';
  };

  const getFearGreedText = (score: number) => {
    if (score >= 75) return 'Extreme Greed';
    if (score >= 50) return 'Greed';
    if (score >= 25) return 'Fear';
    return 'Extreme Fear';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600">Market Cap</h3>
          <DollarSign className="w-5 h-5 text-blue-500" />
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">
            {formatLargeNumber(3.1e12)}
          </span>
          <span className="text-sm font-medium text-green-600">+0.62%</span>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600">24h Volume</h3>
          <Activity className="w-5 h-5 text-purple-500" />
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">
            {formatLargeNumber(180.69e9)}
          </span>
          <span className="text-sm font-medium text-green-600">+5.14%</span>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600">Dominance</h3>
          <PieChart className="w-5 h-5 text-orange-500" />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <img src="https://assets.coingecko.com/coins/images/1/small/bitcoin.png" 
                   alt="BTC" 
                   className="w-5 h-5" />
              <span className="text-gray-600">BTC</span>
            </span>
            <span className="font-bold text-gray-900">59.45%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <img src="https://assets.coingecko.com/coins/images/279/small/ethereum.png" 
                   alt="ETH" 
                   className="w-5 h-5" />
              <span className="text-gray-600">ETH</span>
            </span>
            <span className="font-bold text-gray-900">12.04%</span>
          </div>
        </div>
      </GlassCard>

      <GlassCard className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-600">Fear & Greed</h3>
          <Thermometer className="w-5 h-5 text-red-500" />
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className={`text-2xl font-bold ${getFearGreedColor(fearGreedScore)}`}>
              {fearGreedScore}
            </span>
            <span className={`text-sm font-medium ${getFearGreedColor(fearGreedScore)}`}>
              {getFearGreedText(fearGreedScore)}
            </span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${getFearGreedColor(fearGreedScore)} bg-current transition-all`}
              style={{ width: `${fearGreedScore}%` }}
            />
          </div>
        </div>
      </GlassCard>
    </div>
  );
}